import { useRef, useState } from 'react';
import { attemptKey, grade } from '../domain.ts';
import type { Progress, Question } from '../types.ts';

export function QuestionCard({
  question,
  index,
  context,
  progress,
  update,
  onRead,
  onSubmit,
}: {
  question: Question;
  index: number;
  context: string;
  progress: Progress;
  update: (fn: (value: Progress) => Progress) => void;
  onRead: () => void;
  onSubmit: (input: string) => void;
}) {
  const key = attemptKey(context, question.id);
  const attempt = progress.attempts[key];
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState('');
  const input = progress.drafts[key] ?? '';
  const answered = Boolean(attempt) && !retrying;
  const field = useRef<HTMLFieldSetElement>(null);
  function setInput(value: string) {
    setError('');
    update((current) => ({ ...current, drafts: { ...current.drafts, [key]: value } }));
  }
  return (
    <section className="question" aria-labelledby={`prompt-${question.id}`}>
      <span className="question-number" aria-hidden="true">
        {String(index + 1).padStart(2, '0')}
      </span>
      <div className="question-content">
        <h2 id={`prompt-${question.id}`} tabIndex={-1}>
          {question.prompt}
        </h2>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (grade(question, input) === null) {
              setError(
                question.answer.kind === 'choice'
                  ? 'Choose an answer first.'
                  : 'Enter a number, fraction, or percentage, such as 0.5, 1/2, or 50%.',
              );
              return;
            }
            onSubmit(input);
            setRetrying(false);
          }}
        >
          <fieldset ref={field} disabled={answered} aria-labelledby={`prompt-${question.id}`}>
            {question.answer.kind === 'choice' ? (
              <div className="choices">
                {question.options?.map((option, i) => (
                  <label
                    key={option.id}
                    className={input === option.id ? 'option checked' : 'option'}
                  >
                    <input
                      type="radio"
                      name={question.id}
                      value={option.id}
                      checked={input === option.id}
                      onChange={() => setInput(option.id)}
                    />
                    <span className="choice-letter" aria-hidden="true">
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span>{option.text}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="number-answer">
                <label htmlFor={`answer-${question.id}`}>Your answer</label>
                <div>
                  <input
                    id={`answer-${question.id}`}
                    type="text"
                    inputMode="text"
                    autoComplete="off"
                    spellCheck={false}
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    aria-describedby={error ? `error-${question.id}` : undefined}
                  />
                  <span className="quiet">{question.answer.unit}</span>
                </div>
              </div>
            )}
          </fieldset>
          {error && (
            <p className="input-error" id={`error-${question.id}`} role="alert">
              {error}
            </p>
          )}
          {!answered && (
            <button className="primary submit" type="submit">
              Check answer
            </button>
          )}
        </form>
        {answered && (
          <div className="feedback" role="status">
            {attempt?.correct ? (
              <>
                <span className="correct">Correct.</span>
                <button className="text-button" onClick={onRead}>
                  Read the explanation ↗
                </button>
              </>
            ) : (
              <>
                <span>Not quite.</span>
                <button
                  onClick={() => {
                    setRetrying(true);
                    setError('');
                    setTimeout(() => field.current?.querySelector('input')?.focus(), 0);
                  }}
                >
                  Retry
                </button>
                <button className="text-button" onClick={onRead}>
                  Read associated material ↗
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
